import {LABELS} from '../constants/labels'
import {timelineItemKey} from '../utils/timeline-item-key'
import {type DemilestonedEventFragment, getWrappedMilestoneLink} from './DemilestonedEvent'
import type {MilestonedEventFragment} from './MilestonedEvent'

type MilestoneEventFragment = MilestonedEventFragment | DemilestonedEventFragment

type RolledupMilestonedEventProps = {
  rollupGroup: Record<string, MilestoneEventFragment[]>
}

type MilestoneLinkProps = {
  milestoneEventKey: MilestoneEventFragment
  addDelimiter: boolean
}

type WithAttributes<T> = T & {
  id: string
  // __index is used for sorting the events
  __index: number
  // milestone is used to remove duplicates when it exists
  milestone?: {
    id: string
  }
  // milestoneTitle is used as a fallback for milestone?.id as the milestone may have been deleted
  milestoneTitle: string
}

export function RolledupMilestonedEvent({rollupGroup}: RolledupMilestonedEventProps): React.ReactElement {
  const addedEvents = (rollupGroup['MilestonedEvent'] as Array<WithAttributes<MilestonedEventFragment>>) || []
  const removedEvents = (rollupGroup['DemilestonedEvent'] as Array<WithAttributes<DemilestonedEventFragment>>) || []

  const sortedEvents = getSortedEvents(addedEvents, removedEvents)

  const prefix = sortedEvents.length > 1 ? LABELS.timeline.modifiedMilestones : LABELS.timeline.modifiedMilestone

  return (
    <>
      {`${prefix} `}
      {sortedEvents.map((event, index) => (
        <MilestoneLink
          milestoneEventKey={event}
          key={timelineItemKey(event)}
          addDelimiter={index < sortedEvents.length - 1}
        />
      ))}
    </>
  )
}

// This function is used to sort the events by their index in the timeline and remove duplicates by milestone id (only the first event is kept)
function getSortedEvents(
  addedEvents: Array<WithAttributes<MilestonedEventFragment>>,
  removedEvents: Array<WithAttributes<DemilestonedEventFragment>>,
) {
  const eventOrder: Record<string, number> = {}
  const eventKeys: Record<string, WithAttributes<MilestonedEventFragment> | WithAttributes<DemilestonedEventFragment>> =
    {}

  const allEvents = [...addedEvents, ...removedEvents]

  for (const event of allEvents) {
    const id = event.milestone?.id || event.milestoneTitle
    const eventIndex = eventOrder[id]
    if (eventIndex === undefined || event.__index < eventIndex) {
      eventOrder[id] = event.__index
      eventKeys[id] = event
    }
  }

  return Object.entries(eventOrder)
    .sort((a, b) => a[1] - b[1])
    .map(([id]) => eventKeys[id])
    .filter(event => event !== undefined)
}

function MilestoneLink({milestoneEventKey, addDelimiter}: MilestoneLinkProps) {
  return getWrappedMilestoneLink(milestoneEventKey.milestone?.url, milestoneEventKey.milestoneTitle, addDelimiter)
}
